import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Home, Building2, HardHat, ArrowLeft, MailCheck } from 'lucide-react';

import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import Turnstile from '@/components/Turnstile';
import ResendConfirmation from '@/components/ResendConfirmation';
import { guardAuth, guardMessage, reportAuthFailure } from '@/lib/authGuard';

import { MarketplaceDisclaimer, AcceptTermsCheckbox } from '@/components/MarketplaceDisclaimer';
import { ACCEPT_REQUIRED, TERMS_VERSION, bi } from '@/data/legal';
import { TRADES, US_STATES, UserType, tradeLabel, homeownerCategories, gcTrades } from '@/data/constants';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

const Register: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = (params.get('role') as UserType) || null;
  const [role, setRole] = useState<UserType | null>(initialRole);
  /**
   * COMMERCIAL INTENT. The homepage "Commercial" card links here with
   * ?role=homeowner&type=commercial. There is NO fourth account type — a
   * commercial property owner is a project-owner account that posts projects
   * with project_type='commercial'. This flag only changes the heading and adds
   * an explanatory note, so nothing about auth, RLS or the profile row changes.
   */
  const commercial = params.get('type') === 'commercial';

  /**
   * `next` lets a flow resume after account creation — the post-payment return
   * page uses it so a buyer WITHOUT an account activates it here and is sent
   * straight back to /payment-complete, which then applies the paid credits and
   * continues to Projects. `email` pre-fills the address that was paid with, so
   * the verified webhook can be matched to this new account.
   */
  const nextUrl = params.get('next') || '';
  const [f, setF] = useState<Record<string, string>>(() => {
    const prefill = params.get('email') || '';
    return prefill ? { email: prefill } : {};
  });

  const [trades, setTrades] = useState<string[]>([]);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Turnstile token — re-verified server-side by auth-guard before signUp runs.
  const [captcha, setCaptcha] = useState('');
  /**
   * True once Supabase has created the auth user but is waiting for the email
   * address to be confirmed. Drives the confirmation screen below.
   */
  const [pending, setPending] = useState(false);



  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const toggleTrade = (slug: string) =>
    setTrades((p) => (p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]));

  const roles = [
    { key: 'homeowner' as UserType, icon: Home, title: t('home.homeownerTitle'), desc: t('home.homeownerDesc') },
    { key: 'gc' as UserType, icon: Building2, title: t('home.gcTitle'), desc: t('home.gcDesc') },
    { key: 'subcontractor' as UserType, icon: HardHat, title: t('home.subTitle'), desc: t('home.subDesc') },
  ];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\S+@\S+\.\S+$/.test(f.email || '')) return setError(t('auth.errEmail'));
    if ((f.password || '').length < 6) return setError(t('auth.errPassword'));
    if (role === 'homeowner' && (!f.first_name || !f.last_name || !f.phone || !f.city || !f.state))
      return setError(t('auth.errInvalid'));
    // Company accounts: Company Name, Owner/Contact Name, Email, Phone, City,
    // State, Service Areas and Trade Categories. Licensing is NOT collected here.
    if (role !== 'homeowner' && (!f.company_name || !f.contact_name || !f.phone || !f.city || !f.state || !f.service_areas))
      return setError(t('auth.errInvalid'));
    if (role === 'subcontractor' && (!f.years_in_business || !f.employees)) return setError(t('auth.errInvalid'));
    if (role !== 'homeowner' && trades.length === 0) return setError(t('auth.errTrades'));
    if (!accepted) return setError(bi(ACCEPT_REQUIRED, lang));

    setBusy(true);

    // Automated-signup defence: server-verified Turnstile + 5 registrations
    // per IP per hour, with every rejection logged to mkt_security_events.
    const verdict = await guardAuth('register', f.email, captcha);
    if (!verdict.ok) {
      setBusy(false);
      return setError(guardMessage(verdict, t));
    }

    const res = await signUp(f.email, f.password, {
      user_type: role || 'subcontractor',
      first_name: f.first_name,
      last_name: f.last_name,
      company_name: f.company_name,
      contact_name: f.contact_name,
      phone: f.phone,
      city: f.city,
      state: f.state,
      years_in_business: f.years_in_business ? Number(f.years_in_business) : null,
      employees: f.employees ? Number(f.employees) : null,
      service_areas: (f.service_areas || '').split(',').map((s) => s.trim()).filter(Boolean),
      trades,
      preferred_language: lang,
      terms_accepted_at: new Date().toISOString(),
      terms_version: TERMS_VERSION,
    });
    setBusy(false);
    if (res.error) {
      await reportAuthFailure('register', f.email);
      return setError(res.error);
    }

    /**
     * EMAIL CONFIRMATION REQUIRED.  (P0 fix — "I never got a confirmation")
     *
     * Previously ANY non-error result navigated straight to /dashboard. When
     * Supabase requires address confirmation there is no session yet, so the
     * profile row could not be written, the dashboard bounced the user back to
     * /login, and nothing on screen ever said "check your email". People
     * reasonably concluded the account was created when it was not.
     *
     * Now the registration turns into an explicit confirmation step.
     */
    if (res.needsConfirmation) {
      setPending(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Resume the interrupted flow (post-payment activation) when asked to.
    navigate(nextUrl || path('/dashboard'));

  };

  /**
   * CONFIRMATION STEP. Shown only when Supabase created the auth user but is
   * waiting for the address to be confirmed. It states plainly what happened,
   * where the email went, and what to do if it does not arrive — instead of
   * the old silent redirect that made a pending signup look like a finished
   * account.
   */
  if (pending) {
    return (
      <div className="mx-auto max-w-xl px-4 py-14 sm:px-6">
        <Seo title={lang === 'es' ? 'Confirme su correo' : 'Confirm your email'} description="" noindex />
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6 sm:p-8">
          <p className="flex items-center gap-2 text-xl font-extrabold text-emerald-900">
            <MailCheck className="h-6 w-6" />
            {lang === 'es' ? 'Revise su correo' : 'Check your email'}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-emerald-900">
            {lang === 'es'
              ? 'Enviamos un enlace de confirmación a:'
              : 'We sent a confirmation link to:'}
          </p>
          <p className="mt-1 break-all rounded-lg bg-white px-3 py-2 text-sm font-bold text-slate-900">
            {f.email}
          </p>
          <p className="mt-4 text-sm leading-relaxed text-emerald-900">
            {lang === 'es'
              ? 'Su cuenta NO estará activa hasta que abra ese enlace. Después de confirmarla podrá iniciar sesión y usar su panel.'
              : 'Your account is NOT active until you open that link. Once confirmed you can sign in and use your dashboard.'}
          </p>
          <ul className="mt-4 space-y-1.5 text-sm text-emerald-800">
            <li>
              {lang === 'es'
                ? '• Si no llega en unos minutos, revise la carpeta de spam o correo no deseado.'
                : '• If it does not arrive within a few minutes, check your spam or junk folder.'}
            </li>
            <li>
              {lang === 'es'
                ? '• El enlace abre GIGGONE en este mismo navegador.'
                : '• The link opens GIGGONE in this same browser.'}
            </li>
          </ul>

          {/*
            RESEND — same component, same 60-second cooldown and same
            server-side rate limit as the sign-in page, so the two recovery
            paths can never drift apart.
          */}
          <ResendConfirmation email={f.email || ''} lang={lang} className="mt-5" />

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={path('/login')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-700"
            >
              {lang === 'es' ? 'Ir a iniciar sesión' : 'Go to sign in'}
            </Link>
            <Link
              to={path('/jobs')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl border-2 border-emerald-300 px-5 py-3 text-sm font-bold text-emerald-800 hover:bg-emerald-100"
            >
              {lang === 'es' ? 'Ver proyectos' : 'Browse projects'}
            </Link>
          </div>
          <p className="mt-5 text-xs text-emerald-800">
            {lang === 'es' ? '¿Correo equivocado? ' : 'Wrong address? '}
            <button
              type="button"
              onClick={() => setPending(false)}
              className="font-bold underline"
            >
              {lang === 'es' ? 'Volver y corregirlo' : 'Go back and fix it'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (!role) {

    return (
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <Seo title={t('auth.chooseRole')} description={t('auth.chooseRoleSub')} noindex />
        <h1 className="text-center text-3xl font-extrabold tracking-tight text-slate-900">{t('auth.chooseRole')}</h1>
        <p className="mt-2 text-center text-slate-500">{t('auth.chooseRoleSub')}</p>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {roles.map((r) => (
            <button
              key={r.key}
              onClick={() => setRole(r.key)}
              className="rounded-2xl border border-slate-200 bg-white p-6 text-left transition-all hover:-translate-y-1 hover:border-orange-300 hover:shadow-lg"
            >
              <r.icon className="h-8 w-8 text-orange-500" />
              <h2 className="mt-4 text-lg font-bold text-slate-900">{r.title}</h2>
              <p className="mt-2 text-sm text-slate-600">{r.desc}</p>
            </button>
          ))}
        </div>
        <MarketplaceDisclaimer variant="short" className="mt-8" />
        <p className="mt-8 text-center text-sm text-slate-500">
          {t('auth.haveAccount')}{' '}
          <Link to={path('/login')} className="font-bold text-orange-600">{t('nav.login')}</Link>
        </p>
      </div>
    );
  }

  const heading =
    role === 'homeowner'
      ? commercial
        ? t('auth.registerCommercial')
        : t('auth.registerHomeowner')
      : role === 'gc'
      ? t('auth.registerGc')
      : t('auth.registerSub');
  const tradeOptions = role === 'gc' ? gcTrades() : role === 'homeowner' ? homeownerCategories() : TRADES;

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <Seo title={heading} description={t('auth.chooseRoleSub')} noindex />
      <button onClick={() => setRole(null)} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <h1 className="text-2xl font-extrabold text-slate-900">{heading}</h1>
        {/* Commercial intent: explain that the SAME free project-owner account
            covers commercial work, so nobody hunts for an account type that
            does not exist. */}
        {role === 'homeowner' && commercial && (
          <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-slate-700">{t('auth.commercialNote')}</p>
        )}
        {role !== 'homeowner' && (
          <p className="mt-2 text-sm text-slate-500">
            {lang === 'es'
              ? 'No se requiere número de licencia para registrarse. Puede agregarlo después en Configuración de Perfil si lo desea.'
              : 'No license number is required to register. You can add one later in Profile Settings if you want to.'}
          </p>
        )}

        <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">
          {role === 'homeowner' ? (
            <>
              <div><label className={labelCls}>{t('auth.firstName')}</label><input className={inputCls} value={f.first_name || ''} onChange={(e) => set('first_name', e.target.value)} /></div>
              <div><label className={labelCls}>{t('auth.lastName')}</label><input className={inputCls} value={f.last_name || ''} onChange={(e) => set('last_name', e.target.value)} /></div>
            </>
          ) : (
            <>
              <div className="sm:col-span-2"><label className={labelCls}>{t('auth.companyName')}</label><input className={inputCls} value={f.company_name || ''} onChange={(e) => set('company_name', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>{role === 'gc' ? t('auth.contactName') : t('auth.ownerName')}</label><input className={inputCls} value={f.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} /></div>
            </>
          )}

          <div><label className={labelCls}>{t('common.email')}</label><input type="email" className={inputCls} value={f.email || ''} onChange={(e) => set('email', e.target.value)} /></div>
          <div><label className={labelCls}>{t('common.phone')}</label><input type="tel" className={inputCls} value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} /></div>
          <div><label className={labelCls}>{t('common.city')}</label><input className={inputCls} value={f.city || ''} onChange={(e) => set('city', e.target.value)} /></div>
          <div>
            <label className={labelCls}>{t('common.state')}</label>
            <select className={inputCls} value={f.state || ''} onChange={(e) => set('state', e.target.value)}>
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {role === 'subcontractor' && (
            <>
              <div><label className={labelCls}>{t('auth.years')}</label><input type="number" min="0" className={inputCls} value={f.years_in_business || ''} onChange={(e) => set('years_in_business', e.target.value)} /></div>
              <div><label className={labelCls}>{t('auth.employees')}</label><input type="number" min="0" className={inputCls} value={f.employees || ''} onChange={(e) => set('employees', e.target.value)} /></div>
            </>
          )}

          {role !== 'homeowner' && (
            <>
              <div className="sm:col-span-2"><label className={labelCls}>{t('auth.serviceAreas')}</label><input className={inputCls} value={f.service_areas || ''} onChange={(e) => set('service_areas', e.target.value)} placeholder="Fontana, Riverside, Ontario" /></div>
              <div className="sm:col-span-2">
                <label className={labelCls}>{role === 'gc' ? t('auth.tradesNeeded') : t('auth.tradeCategories')}</label>
                <p className="mb-2 text-xs text-slate-400">{t('auth.selectTrades')}</p>
                <div className="flex flex-wrap gap-2">
                  {tradeOptions.map((tr) => (
                    <button
                      type="button"
                      key={tr.slug}
                      onClick={() => toggleTrade(tr.slug)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                        trades.includes(tr.slug) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600'
                      }`}
                    >
                      {tradeLabel(tr.slug, lang)}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="sm:col-span-2"><label className={labelCls}>{t('auth.password')}</label><input type="password" className={inputCls} value={f.password || ''} onChange={(e) => set('password', e.target.value)} /></div>

          <div className="sm:col-span-2">
            <MarketplaceDisclaimer variant="full" role={role} />
          </div>

          <div className="sm:col-span-2">
            <AcceptTermsCheckbox checked={accepted} onChange={setAccepted} kind="register" />
          </div>

          <div className="sm:col-span-2">
            <Turnstile action="register" onVerify={setCaptcha} label={t('auth.captchaLabel')} />
          </div>

          {error && <p className="sm:col-span-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={busy || !accepted}
              className="w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-50"
            >
              {busy ? t('auth.creating') : t('auth.createAccount')}
            </button>
            <p className="mt-3 text-center text-sm text-slate-500">
              {t('auth.haveAccount')} <Link to={path('/login')} className="font-bold text-orange-600">{t('nav.login')}</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
